/**
 * Theme Provider Context
 *
 * Manages named themes via data-theme attribute on <html>.
 * Matches the ai-signal app's theme system for visual consistency.
 *
 * Themes: light, github-dark, midnight, nord, dracula, ocean, solarized, rose, forest
 * Persists to localStorage under 'compass-theme'.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

export interface ThemeOption {
  id: string;
  label: string;
  isDark: boolean;
}

export const THEMES: ThemeOption[] = [
  { id: 'light',       label: 'Light',      isDark: false },
  { id: 'github-dark', label: 'GitHub Dark', isDark: true },
  { id: 'midnight',    label: 'Midnight',   isDark: true },
  { id: 'nord',        label: 'Nord',       isDark: true },
  { id: 'dracula',     label: 'Dracula',    isDark: true },
  { id: 'ocean',       label: 'Ocean',      isDark: true },
  { id: 'solarized',   label: 'Solarized',  isDark: false },
  { id: 'rose',        label: 'Rose',       isDark: false },
  { id: 'forest',      label: 'Forest',     isDark: true },
];

const DEFAULT_THEME = 'light';
const STORAGE_KEY = 'compass-theme';

interface ThemeContextValue {
  theme: string;
  themeOption: ThemeOption;
  setTheme: (id: string) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function applyTheme(id: string) {
  document.documentElement.setAttribute('data-theme', id);
  // Also toggle Tailwind's dark class for any remaining dark: utilities
  const option = THEMES.find((t) => t.id === id);
  if (option?.isDark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

function loadTheme(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState(loadTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((id: string) => {
    const valid = THEMES.find((t) => t.id === id);
    if (!valid) return;
    setThemeState(id);
    applyTheme(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch {}
  }, []);

  // THEMES is non-empty by construction (seeded list); the `!` keeps
  // noUncheckedIndexedAccess strict mode happy without weakening the type.
  const themeOption = THEMES.find((t) => t.id === theme) || THEMES[0]!;

  return (
    <ThemeContext.Provider value={{ theme, themeOption, setTheme, isDark: themeOption.isDark }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
